const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .toLowerCase();

const SENSOR_TYPE_ALIASES = {
  fuel_level: "fuel",
  tyre_pressure: "pressure",
};

export const normalizeSensorType = (value) => {
  const key = normalizeKey(value);
  return SENSOR_TYPE_ALIASES[key] || key;
};

// Used only when the device registry has not configured a unit for the sensor.
const SENSOR_UNITS = {
  temperature: "\u00B0C",
  humidity: "%",
  fuel: "L",
  vibration: "m/s\u00B2",
  smoke: "ppm",
  pressure: "psi",
  load_weight: "kg",
  engine_health: "%",
  battery_voltage: "V",
};

// Used only when the device registry has not configured min/max for the sensor,
// so the table can still colour readings against a threshold.
export const DEFAULT_SENSOR_RANGES = {
  temperature: { min: 2, max: 8 },
  humidity: { min: 30, max: 70 },
  fuel: { min: 5, max: 100 },
  vibration: { min: 0, max: 1.5 },
  smoke: { min: 0, max: 10 },
  pressure: { min: 0, max: 150 },
  ignition: { min: 0, max: 1 },
  battery_voltage: { min: 12.6, max: 14.5 },
  engine_health: { min: 0, max: 100 },
  load_weight: { min: 0, max: 5000 },
};

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

/**
 * Maps one `GET /device/monitor/latest` reading onto the shape the monitor
 * table renders. The reading value is passed through untouched; a device with
 * no reading yet stays without `val` so the table shows it as assigned only.
 */
const toMonitorDevice = (reading) => {
  const sensorType = reading?.sensorType
    ? normalizeSensorType(reading.sensorType)
    : null;
  const defaults = DEFAULT_SENSOR_RANGES[sensorType] || {};

  return {
    trackingDeviceId: reading?.trackingDeviceId,
    type: sensorType || normalizeKey(reading?.deviceType),
    id: reading?.deviceId || String(reading?.trackingDeviceId || ""),
    unit: reading?.unit || SENSOR_UNITS[sensorType] || "",
    val: toNumber(reading?.value),
    min: toNumber(reading?.minValue) ?? defaults.min,
    max: toNumber(reading?.maxValue) ?? defaults.max,
    status: reading?.status || "",
    workingCondition: reading?.workingCondition || "",
    recordedAt: reading?.recordedAt || null,
    warningPercentage: toNumber(reading?.warningMargin) ?? 5,
    latitude: toNumber(reading?.latitude ?? reading?.lat),
    longitude: toNumber(reading?.longitude ?? reading?.lng ?? reading?.lon),
  };
};

const groupReadingsBy = (readings, key) => {
  const grouped = new Map();

  readings.forEach((reading) => {
    const groupId = reading?.[key];
    if (!groupId) return;

    const groupKey = String(groupId);
    const devices = grouped.get(groupKey) || [];
    devices.push(toMonitorDevice(reading));
    grouped.set(groupKey, devices);
  });

  return grouped;
};

export const buildVehicleMonitorRows = (vehicles = [], readings = []) => {
  const devicesByVehicleId = groupReadingsBy(readings, "vehicleId");

  return vehicles.map((vehicle) => ({
    id: vehicle.id,
    name: vehicle.name || "Vehicle",
    number: vehicle.VehicleNumber || vehicle.registrationNumber || "",
    status: vehicle.status || "INACTIVE",
    make: vehicle.Make || vehicle.make || "",
    model: vehicle.Model || vehicle.model || "",
    year: vehicle.Year || vehicle.year || "",
    latitude:
      vehicle.latitude ?? vehicle.Latitude ?? vehicle.lat ?? null,
    longitude:
      vehicle.longitude ?? vehicle.Longitude ?? vehicle.lng ?? vehicle.lon ?? null,
    device_id: vehicle.device_id || vehicle.deviceId || null,
    devices: devicesByVehicleId.get(String(vehicle.id)) || [],
  }));
};

export const buildStopMonitorRows = (
  stops = [],
  readings = [],
  assignedStopIds = new Set(),
) => {
  const devicesByStopId = new Map();
  const stopNames = new Map();

  readings.forEach((reading) => {
    const groupId = reading?.stopId || reading?.stop_id;
    if (!groupId) return;
    const groupKey = String(groupId);
    const devices = devicesByStopId.get(groupKey) || [];
    devices.push(toMonitorDevice(reading));
    devicesByStopId.set(groupKey, devices);
    stopNames.set(groupKey, reading.stopName || reading.stop_name || "Stop");
  });

  const devicesForStop = (stop) => {
    const byNumericId = devicesByStopId.get(String(stop?.id)) || [];
    const byStopCode = devicesByStopId.get(String(stop?.stopId || "")) || [];
    if (!byStopCode.length) return byNumericId;

    const seen = new Set(byNumericId.map((device) => String(device.id)));
    return [
      ...byNumericId,
      ...byStopCode.filter((device) => !seen.has(String(device.id))),
    ];
  };

  if (Array.isArray(stops) && stops.length) {
    return stops.map((stop) => {
      const devices = devicesForStop(stop);
      const assigned =
        assignedStopIds.has(String(stop.id || "")) ||
        assignedStopIds.has(String(stop.stopId || "")) ||
        (Array.isArray(stop.routes) && stop.routes.length > 0) ||
        (Array.isArray(stop.assignedRoutes) && stop.assignedRoutes.length > 0) ||
        Boolean(stop.routeId || stop.assignedRouteId);
      return {
        id: stop.id,
        name: stop.name || stop.stopId || "Stop",
        number: stop.stopId || "",
        status: assigned ? "ACTIVE" : "UNASSIGNED",
        devices,
      };
    });
  }

  return Array.from(devicesByStopId.entries()).map(([stopId, devices]) => ({
    id: stopId,
    name: stopNames.get(stopId) || "Stop",
    number: "",
    status: assignedStopIds.has(String(stopId)) ? "ACTIVE" : "UNASSIGNED",
    devices,
  }));
};
